import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Key, FileKey, Check } from 'lucide-react';
import { certificateApi } from '../services/api';

const GenerateCertificate = () => {
  const navigate = useNavigate();
  const [model, setModel] = useState('self-signed'); // 'self-signed' or 'ca-signed'
  const [step, setStep] = useState(1); // For CA-signed: 1=generate CSR, 2=import cert
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [csrResult, setCsrResult] = useState(null);
  
  const [formData, setFormData] = useState({
    alias: '',
    commonName: '',
    organization: '',
    organizationalUnit: '',
    locality: '',
    state: '',
    country: 'RW',
    keyAlgorithm: 'RSA',
    keySize: 2048,
    validityDays: 730
  });

  const [caImportData, setCaImportData] = useState({
    certificatePem: '',
    caName: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenerateSelfSigned = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await certificateApi.generateCertificate(formData);
      alert('Certificate generated successfully!');
      navigate(`/certificates/${result.id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCSR = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await certificateApi.generateCSR(formData);
      setCsrResult(result);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate CSR');
    } finally {
      setLoading(false);
    }
  };

  const handleImportCACertificate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await certificateApi.importCACertificate(
        csrResult.csrId,
        caImportData.certificatePem,
        caImportData.caName
      );
      alert('CA-signed certificate imported successfully!');
      navigate(`/certificates/${result.id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to import certificate');
    } finally {
      setLoading(false);
    }
  };

  const copyCSR = () => {
    navigator.clipboard.writeText(csrResult.csrPem);
    alert('CSR copied to clipboard!');
  };

  const downloadCSR = () => {
    const blob = new Blob([csrResult.csrPem], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${formData.alias || 'certificate'}.csr`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/certificates')}
          className="btn-secondary"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Generate Certificate</h1>
          <p className="text-gray-600 mt-1">Create a new signing certificate</p>
        </div>
      </div>

      {/* Model Selection */}
      {!csrResult && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button
            onClick={() => setModel('self-signed')}
            className={`p-6 rounded-lg border-2 transition-all ${
              model === 'self-signed'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start space-x-4">
              <Shield className={`w-8 h-8 ${
                model === 'self-signed' ? 'text-primary-600' : 'text-gray-400'
              }`} />
              <div className="text-left flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Model 1: Self-Signed</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Generate a complete certificate immediately. Suitable for testing and internal use.
                </p>
                <ul className="mt-3 space-y-1 text-xs text-gray-500">
                  <li>✓ Instant generation</li>
                  <li>✓ No external CA required</li>
                  <li>✓ Full control over certificate</li>
                </ul>
              </div>
            </div>
          </button>

          <button
            onClick={() => setModel('ca-signed')}
            className={`p-6 rounded-lg border-2 transition-all ${
              model === 'ca-signed'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start space-x-4">
              <FileKey className={`w-8 h-8 ${
                model === 'ca-signed' ? 'text-primary-600' : 'text-gray-400'
              }`} />
              <div className="text-left flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Model 2: CA-Signed</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Generate CSR, submit to CA, then import signed certificate. For production use.
                </p>
                <ul className="mt-3 space-y-1 text-xs text-gray-500">
                  <li>✓ Trusted by certificate authorities</li>
                  <li>✓ Production-ready</li>
                  <li>✓ Meets compliance requirements</li>
                </ul>
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Certificate Form (Model 1) */}
      {model === 'self-signed' && !csrResult && (
        <form onSubmit={handleGenerateSelfSigned} className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">
              Certificate Details
            </h2>
          </div>
          <div className="card-body space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alias <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="alias"
                  required
                  className="input"
                  placeholder="e.g., production-2025"
                  value={formData.alias}
                  onChange={handleInputChange}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Friendly name to identify this certificate
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Common Name (CN) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="commonName"
                  required
                  className="input"
                  placeholder="e.g., ABC Bank Payment Gateway"
                  value={formData.commonName}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization (O) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="organization"
                  required
                  className="input"
                  placeholder="e.g., ABC Bank Rwanda"
                  value={formData.organization}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organizational Unit (OU)
                </label>
                <input
                  type="text"
                  name="organizationalUnit"
                  className="input"
                  placeholder="e.g., IT Department"
                  value={formData.organizationalUnit}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Locality (L) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="locality"
                  required
                  className="input"
                  placeholder="e.g., Kigali"
                  value={formData.locality}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State/Province (ST)
                </label>
                <input
                  type="text"
                  name="state"
                  className="input"
                  placeholder="e.g., Kigali City"
                  value={formData.state}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country (C) <span className="text-red-500">*</span>
                </label>
                <select
                  name="country"
                  required
                  className="input"
                  value={formData.country}
                  onChange={handleInputChange}
                >
                  <option value="RW">Rwanda (RW)</option>
                  <option value="KE">Kenya (KE)</option>
                  <option value="UG">Uganda (UG)</option>
                  <option value="TZ">Tanzania (TZ)</option>
                  <option value="LR">Liberia (LR)</option>
                  <option value="GN">Guinea (GN)</option>
                  <option value="ET">Ethiopia (ET)</option>
                  <option value="US">United States (US)</option>
                </select>
              </div>
            </div>

            {/* Cryptographic Parameters */}
            <div className="pt-6 border-t">
              <h3 className="text-md font-semibold text-gray-900 mb-4">
                Cryptographic Parameters
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Key Algorithm
                  </label>
                  <select
                    name="keyAlgorithm"
                    className="input"
                    value={formData.keyAlgorithm}
                    onChange={handleInputChange}
                  >
                    <option value="RSA">RSA</option>
                    <option value="DSA">DSA</option>
                    <option value="EC">EC (Elliptic Curve)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Key Size (bits)
                  </label>
                  <select
                    name="keySize"
                    className="input"
                    value={formData.keySize}
                    onChange={handleInputChange}
                  >
                    <option value="2048">2048</option>
                    <option value="3072">3072</option>
                    <option value="4096">4096</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Validity (days)
                  </label>
                  <input
                    type="number"
                    name="validityDays"
                    min="1"
                    max="3650"
                    className="input"
                    value={formData.validityDays}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/certificates')}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary flex items-center space-x-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="animate-spin">⟳</span>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4" />
                    <span>Generate Certificate</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* CSR Generation Form (Model 2 - Step 1) */}
      {model === 'ca-signed' && step === 1 && !csrResult && (
        <form onSubmit={handleGenerateCSR} className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">
              Step 1: Generate Certificate Signing Request (CSR)
            </h2>
          </div>
          <div className="card-body space-y-6">
            {/* Same form fields as self-signed */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alias <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="alias"
                  required
                  className="input"
                  placeholder="e.g., production-ca-2025"
                  value={formData.alias}
                  onChange={handleInputChange}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Common Name (CN) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="commonName"
                  required
                  className="input"
                  placeholder="e.g., Production Bank Gateway"
                  value={formData.commonName}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization (O) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="organization"
                  required
                  className="input"
                  value={formData.organization}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organizational Unit (OU)
                </label>
                <input
                  type="text"
                  name="organizationalUnit"
                  className="input"
                  value={formData.organizationalUnit}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Locality (L) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="locality"
                  required
                  className="input"
                  value={formData.locality}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country (C) <span className="text-red-500">*</span>
                </label>
                <select
                  name="country"
                  required
                  className="input"
                  value={formData.country}
                  onChange={handleInputChange}
                >
                  <option value="RW">Rwanda (RW)</option>
                  <option value="KE">Kenya (KE)</option>
                  <option value="UG">Uganda (UG)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Key Algorithm
                </label>
                <select
                  name="keyAlgorithm"
                  className="input"
                  value={formData.keyAlgorithm}
                  onChange={handleInputChange}
                >
                  <option value="RSA">RSA</option>
                  <option value="EC">EC</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Key Size (bits)
                </label>
                <select
                  name="keySize"
                  className="input"
                  value={formData.keySize}
                  onChange={handleInputChange}
                >
                  <option value="2048">2048</option>
                  <option value="4096">4096</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/certificates')}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary flex items-center space-x-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="animate-spin">⟳</span>
                    <span>Generating CSR...</span>
                  </>
                ) : (
                  <>
                    <FileKey className="w-4 h-4" />
                    <span>Generate CSR</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* CSR Display & CA Import (Model 2 - Step 2) */}
      {model === 'ca-signed' && step === 2 && csrResult && (
        <div className="space-y-6">
          {/* CSR Display */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-success-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  CSR Generated Successfully
                </h2>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={copyCSR}
                  className="btn-secondary text-sm"
                >
                  Copy CSR
                </button>
                <button
                  onClick={downloadCSR}
                  className="btn-secondary text-sm"
                >
                  Download CSR
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
                <p className="text-sm text-blue-900">
                  {csrResult.instructions}
                </p>
              </div>
              <pre className="bg-gray-900 text-green-400 p-4 rounded font-mono text-xs overflow-x-auto">
                {csrResult.csrPem}
              </pre>
            </div>
          </div>

          {/* CA Certificate Import */}
          <form onSubmit={handleImportCACertificate} className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">
                Step 2: Import CA-Signed Certificate
              </h2>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CA Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  className="input"
                  placeholder="e.g., RSwitch Internal CA"
                  value={caImportData.caName}
                  onChange={(e) => setCaImportData(prev => ({
                    ...prev,
                    caName: e.target.value
                  }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CA-Signed Certificate (PEM) <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows="10"
                  className="input font-mono text-xs"
                  placeholder="-----BEGIN CERTIFICATE-----
...
-----END CERTIFICATE-----"
                  value={caImportData.certificatePem}
                  onChange={(e) => setCaImportData(prev => ({
                    ...prev,
                    certificatePem: e.target.value
                  }))}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Paste the PEM-encoded certificate received from your Certificate Authority
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => navigate('/certificates')}
                  className="btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex items-center space-x-2"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="animate-spin">⟳</span>
                      <span>Importing...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Import Certificate</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default GenerateCertificate;
